import * as React from 'react';
import { useMemo } from 'react';
import notification from '../utils/notification';

import { getCopiedToClipboardMessage } from '../utils/ui';
import useDynamicRoot from './useDynamicRoot';

interface IOptions {
  successDuration?: number;
  toastDuration?: number;
  label?: string;
}

export default function useCopyClipboard({ successDuration, toastDuration, label }: IOptions) {
  const messageContent = useMemo(() => getCopiedToClipboardMessage(label), [label]);

  const [isCopied, setIsCopied] = React.useState(false);
  const messageRef = React.useRef<{ message: string }>({ message: messageContent });
  const elRef = React.useRef<HTMLTextAreaElement | null>(null);
  const root = useDynamicRoot();

  const copy = (str: string) => {
    if (root) {
      elRef.current = document.createElement('textarea');
      const el = elRef.current;
      el.value = str;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      root.appendChild(el);
      const selection = document.getSelection();
      const selected = selection && selection?.rangeCount > 0 ? selection.getRangeAt(0) : false;
      el.select();
      const success = document.execCommand('copy');
      root.removeChild(el);
      if (selected) {
        selection?.removeAllRanges();
        selection?.addRange(selected);
      }
      return success;
    }
    return false;
  };

  React.useEffect(() => {
    if (isCopied && toastDuration) {
      const { message: toastMessage } = messageRef.current;

      notification.info(void 0, {
        content: toastMessage,
        options: {
          onClose: () => (messageRef.current = { message: messageContent }),
        },
      });
    }
  }, [isCopied, toastDuration, messageContent]);

  React.useEffect(() => {
    if (isCopied && successDuration) {
      const id = setTimeout(() => {
        setIsCopied(false);
      }, successDuration);

      return () => {
        clearTimeout(id);
      };
    }
  }, [isCopied, successDuration]);

  return {
    isCopied,
    setCopied: (text: string, toastLabel?: string) => {
      if (!isCopied && text) {
        if (toastLabel) {
          messageRef.current = { message: getCopiedToClipboardMessage(toastLabel) };
        }

        const didCopy = copy(text);
        setIsCopied(didCopy);
      }
    },
  };
}
